import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  fetchAttendanceEventBriefing,
  fetchAttendanceEventLogoutSummary,
  fetchDayActivityTimeline,
  reloginAttendance,
} from '../../services/attendanceService';
import { addMessage, fetchMessages } from '../../store/slices/chatSlice';
import LogToWorkModal from './LogToWorkModal';
import LogoutNowModal from './LogoutNowModal';
import DayActivityTimelineModal from './DayActivityTimelineModal';
import { notifyAttendanceSessionChanged } from '../../services/attendanceSessionBus';
import { addNotification } from '../../store/slices/uiSlice';

const formatEventDate = (eventDate, createdAt) => {
  if (eventDate) {
    try {
      const [y, m, d] = eventDate.split('-').map(Number);
      return new Date(y, m - 1, d).toLocaleDateString(undefined, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return eventDate;
    }
  }
  if (createdAt) {
    try {
      return new Date(createdAt).toLocaleDateString(undefined, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return '';
    }
  }
  return '';
};

const formatDateShort = (eventDate, createdAt) => {
  const source = eventDate
    ? (() => {
      try {
        const [y, m, d] = eventDate.split('-').map(Number);
        return new Date(y, m - 1, d);
      } catch {
        return null;
      }
    })()
    : (createdAt ? new Date(createdAt) : null);
  if (!source || Number.isNaN(source.getTime())) return '';
  return source.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const formatClockWithMinutes = (timestamp) => {
  if (!timestamp) return '';
  try {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '';
  }
};

/** Upgrade legacy labels like "· 11PM IST" to include minutes from message time. */
const withFullClockTime = (label, timestamp, eventTimeLabel) => {
  if (eventTimeLabel && label) {
    // Prefer explicit time label from API when present
    if (/·\s*\d{1,2}(?::\d{2})?\s*(AM|PM)/i.test(label)) {
      return label.replace(/·\s*\d{1,2}(?::\d{2})?\s*(AM|PM)(\s+[A-Z]{2,5})?/i, `· ${eventTimeLabel}`);
    }
  }
  if (!label) return label;
  // Already has minutes
  if (/·\s*\d{1,2}:\d{2}\s*(AM|PM)/i.test(label)) return label;
  const clock = formatClockWithMinutes(timestamp);
  if (!clock) return label;
  // "· 11PM IST" or "· 11 PM IST"
  return label.replace(
    /·\s*\d{1,2}\s*(AM|PM)(\s+[A-Z]{2,5})?/i,
    (match, _ampm, tz) => `· ${clock}${tz || ''}`,
  );
};

const buildLoginLogoutLabel = ({
  isLogout,
  displayName,
  info,
  message,
}) => {
  const timestamp = message?.createdAt || message?.timestamp;
  const stored = info?.label || message?.content || '';
  const upgraded = withFullClockTime(stored, timestamp, info?.eventTimeLabel);
  if (upgraded && /Logged (in|out)/i.test(upgraded)) {
    return upgraded;
  }
  const datePart = formatDateShort(info?.eventDate, timestamp);
  const timePart = info?.eventTimeLabel || formatClockWithMinutes(timestamp) || '';
  const action = isLogout ? 'Logged out' : 'Logged in';
  if (datePart && timePart) {
    return `${displayName} ${action} - ${datePart} · ${timePart}`;
  }
  return upgraded || `${displayName} ${action}`;
};

const AttendanceTimelineButton = ({ message }) => {
  const dispatch = useDispatch();
  const info = message?.attendanceInfo;
  const [briefing, setBriefing] = useState(null);
  const [logoutSummary, setLogoutSummary] = useState(null);
  const [dayTimeline, setDayTimeline] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reloggingIn, setReloggingIn] = useState(false);
  const [error, setError] = useState('');

  if (!info?.attendanceEventId) return null;

  const isLogout = info.eventType === 'CHECK_OUT';
  const isCheckIn = info.eventType === 'CHECK_IN';
  const isRelogin = info.eventType === 'RELOGIN';
  const isBreak = info.eventType === 'BREAK_START' || info.eventType === 'BREAK_END';
  const isBreakEnd = info.eventType === 'BREAK_END';
  const isLeaveApply = info.eventType === 'LEAVE_APPLY';
  const isLeaveRequest = info.eventType === 'LEAVE_REQUEST';
  const isLeaveCancel = info.eventType === 'LEAVE_CANCEL';
  const isLeave = isLeaveApply || isLeaveRequest || isLeaveCancel;
  const displayName = info.eventUserName || message.senderName || 'User';
  const label = (isLogout || isCheckIn)
    ? buildLoginLogoutLabel({ isLogout, displayName, info, message })
    : (info.label
      || message.content
      || (isRelogin
        ? 'Relogged in'
        : isBreak
          ? 'Short break'
          : isLeaveApply
            ? 'Leave applied'
            : isLeaveRequest
              ? 'Leave requested'
              : isLeaveCancel
                ? 'Leave cancelled'
                : 'Logged in'));
  const dateLabel = formatEventDate(info.eventDate, message.createdAt || message.timestamp);

  const subtitleParts = [dateLabel];
  if (isLogout && info.totalTimeSpent) {
    subtitleParts.push(`${info.totalTimeSpent} worked`);
  }
  if (isLogout && info.productivityPercent != null) {
    subtitleParts.push(`${info.productivityPercent}% productivity`);
  }
  if (isBreakEnd && info.totalTimeSpent) {
    subtitleParts.push(`${info.totalTimeSpent} break`);
  }
  if (isRelogin && info.totalTimeSpent) {
    subtitleParts.push(`${info.totalTimeSpent} away`);
  }
  if (isLeave && info.dayType) {
    subtitleParts.push(info.dayType);
  }
  if ((isCheckIn || isLogout) && info.deviceLabel) {
    subtitleParts.push(info.deviceLabel);
  }
  if ((isCheckIn || isLogout) && info.locationLabel) {
    subtitleParts.push(info.locationLabel === 'NA' ? 'Location NA' : info.locationLabel);
  }
  if (isBreak) {
    subtitleParts.push(isBreakEnd ? 'Joined back' : 'Break started');
  } else if (isRelogin) {
    subtitleParts.push('Back to work');
  } else if (isLeaveApply) {
    subtitleParts.push('Leave applied today');
  } else if (isLeaveRequest) {
    subtitleParts.push('Leave request');
  } else if (isLeaveCancel) {
    subtitleParts.push('Leave cancelled');
  } else {
    subtitleParts.push(isLogout ? 'Tap for summary · hold for day line' : 'Tap for briefing · hold for day line');
  }

  const openDayTimeline = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchDayActivityTimeline(info.eventDate, info.eventUserId);
      setDayTimeline(data);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to open day timeline');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = async (e) => {
    e.stopPropagation();
    if (isBreak || isRelogin || isLeave || loading || briefing || logoutSummary || dayTimeline) return;
    setLoading(true);
    setError('');
    try {
      if (isLogout) {
        const data = await fetchAttendanceEventLogoutSummary(info.attendanceEventId);
        setLogoutSummary(data);
      } else {
        const data = await fetchAttendanceEventBriefing(info.attendanceEventId);
        setBriefing(data);
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to open');
    } finally {
      setLoading(false);
    }
  };

  const buttonTheme = isLeave
    ? {
      border: isLeaveApply
        ? 'border-amber-200 dark:border-amber-800 hover:border-amber-400 dark:hover:border-amber-600'
        : 'border-violet-200 dark:border-violet-800 hover:border-violet-400 dark:hover:border-violet-600',
      bg: isLeaveApply
        ? 'from-amber-50 to-orange-50 dark:from-amber-950/50 dark:to-orange-950/40 hover:from-amber-100 hover:to-orange-100 dark:hover:from-amber-900/60 dark:hover:to-orange-900/50'
        : 'from-violet-50 to-indigo-50 dark:from-violet-950/50 dark:to-indigo-950/40 hover:from-violet-100 hover:to-indigo-100 dark:hover:from-violet-900/60 dark:hover:to-indigo-900/50',
      icon: isLeaveApply ? 'from-amber-500 to-orange-600' : 'from-violet-500 to-indigo-600',
      title: isLeaveApply
        ? 'text-amber-900 dark:text-amber-100'
        : 'text-violet-900 dark:text-violet-100',
      sub: isLeaveApply
        ? 'text-amber-700/80 dark:text-amber-300/80'
        : 'text-violet-700/80 dark:text-violet-300/80',
      chevron: isLeaveApply
        ? 'text-amber-600 dark:text-amber-400'
        : 'text-violet-600 dark:text-violet-400',
    }
    : isRelogin
    ? {
      border: 'border-emerald-200 dark:border-emerald-800 hover:border-emerald-400 dark:hover:border-emerald-600',
      bg: 'from-emerald-50 to-teal-50 dark:from-emerald-950/50 dark:to-teal-950/40 hover:from-emerald-100 hover:to-teal-100 dark:hover:from-emerald-900/60 dark:hover:to-teal-900/50',
      icon: 'from-emerald-500 to-teal-600',
      title: 'text-emerald-900 dark:text-emerald-100',
      sub: 'text-emerald-700/80 dark:text-emerald-300/80',
      chevron: 'text-emerald-600 dark:text-emerald-400',
    }
    : isBreak
    ? {
      border: 'border-sky-200 dark:border-sky-800 hover:border-sky-400 dark:hover:border-sky-600',
      bg: 'from-sky-50 to-cyan-50 dark:from-sky-950/50 dark:to-cyan-950/40 hover:from-sky-100 hover:to-cyan-100 dark:hover:from-sky-900/60 dark:hover:to-cyan-900/50',
      icon: 'from-sky-500 to-cyan-600',
      title: 'text-sky-900 dark:text-sky-100',
      sub: 'text-sky-700/80 dark:text-sky-300/80',
      chevron: 'text-sky-600 dark:text-sky-400',
    }
    : isLogout
    ? {
      border: 'border-rose-200 dark:border-rose-800 hover:border-rose-400 dark:hover:border-rose-600',
      bg: 'from-rose-50 to-orange-50 dark:from-rose-950/50 dark:to-orange-950/40 hover:from-rose-100 hover:to-orange-100 dark:hover:from-rose-900/60 dark:hover:to-orange-900/50',
      icon: 'from-rose-500 to-red-600',
      title: 'text-rose-900 dark:text-rose-100',
      sub: 'text-rose-700/80 dark:text-rose-300/80',
      chevron: 'text-rose-600 dark:text-rose-400',
    }
    : {
      border: 'border-emerald-200 dark:border-emerald-800 hover:border-emerald-400 dark:hover:border-emerald-600',
      bg: 'from-emerald-50 to-teal-50 dark:from-emerald-950/50 dark:to-teal-950/40 hover:from-emerald-100 hover:to-teal-100 dark:hover:from-emerald-900/60 dark:hover:to-teal-900/50',
      icon: 'from-emerald-500 to-teal-600',
      title: 'text-emerald-900 dark:text-emerald-100',
      sub: 'text-emerald-700/80 dark:text-emerald-300/80',
      chevron: 'text-emerald-600 dark:text-emerald-400',
    };

  return (
    <>
      <div className="w-full flex flex-col items-center gap-1 py-1">
        <button
          type="button"
          onClick={handleOpen}
          onContextMenu={(e) => {
            if (isCheckIn || isLogout) {
              e.preventDefault();
              e.stopPropagation();
              openDayTimeline();
            }
          }}
          disabled={loading}
          className={`group inline-flex items-center gap-2.5 max-w-full rounded-xl border ${buttonTheme.border} bg-gradient-to-r ${buttonTheme.bg} px-4 py-2.5 text-left shadow-sm hover:shadow-md transition-all disabled:opacity-70 disabled:cursor-wait`}
          title={isBreak || isRelogin || isLeave ? label : isLogout ? 'Open logout summary (right-click: day timeline)' : 'Open morning briefing (right-click: day timeline)'}
        >
          <span className={`flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br ${buttonTheme.icon} text-white flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform`}>
            {loading ? (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : isLeave ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            ) : isRelogin ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : isBreak ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : isLogout ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
          </span>
          <span className="min-w-0 flex-1">
            <span className={`block text-sm font-semibold ${buttonTheme.title} break-words`}>
              {label}
            </span>
            <span className={`block text-[11px] ${buttonTheme.sub} mt-0.5`}>
              {loading ? 'Opening…' : subtitleParts.filter(Boolean).join(' · ')}
            </span>
          </span>
          {!isBreak && !isRelogin && !isLeave && (
            <svg className={`w-4 h-4 flex-shrink-0 ${buttonTheme.chevron} opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          )}
        </button>
        {error && (
          <p className="text-xs text-red-600 dark:text-red-400 px-2">{error}</p>
        )}
      </div>

      {briefing && (
        <LogToWorkModal
          briefing={briefing}
          userName={displayName.split(' ')[0] || displayName}
          readOnly
          onClose={() => setBriefing(null)}
        />
      )}

      {dayTimeline && (
        <DayActivityTimelineModal
          timeline={dayTimeline}
          onClose={() => setDayTimeline(null)}
          onChangeDate={async (nextDate) => {
            const data = await fetchDayActivityTimeline(nextDate, info.eventUserId);
            setDayTimeline(data);
          }}
        />
      )}

      {logoutSummary && (
        <LogoutNowModal
          summary={logoutSummary}
          userName={displayName.split(' ')[0] || displayName}
          readOnly
          onRelogin={logoutSummary.canRelogin ? async () => {
            setReloggingIn(true);
            setError('');
            try {
              const result = await reloginAttendance();
              const channelId = message?.channelId || message?.attendanceInfo?.channelId;
              if (result?.message && channelId) {
                dispatch(addMessage({
                  channelId,
                  message: result.message,
                }));
              }
              if (channelId) {
                dispatch(fetchMessages({ channelId, page: 0 }));
              }
              setLogoutSummary(null);
              notifyAttendanceSessionChanged({ status: result?.status || null });
              dispatch(addNotification({
                type: 'success',
                message: result?.durationLabel
                  ? `Relogged in · ${result.durationLabel} counted as break`
                  : 'Relogged in — you are back to active work',
              }));
            } catch (err) {
              setError(err?.response?.data?.error || err?.message || 'Failed to relogin');
              notifyAttendanceSessionChanged();
              throw err;
            } finally {
              setReloggingIn(false);
            }
          } : undefined}
          onClose={() => setLogoutSummary(null)}
        />
      )}
    </>
  );
};

export default AttendanceTimelineButton;
